import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Check } from 'lucide-react';

import EvaluationLayout from '../../components/EvaluationLayout';
import Button from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { useAuth } from '../../contexts/AuthContext';
import wizardService from '../../services/wizardService';

// Import wizard step components
import TemplateSelection from './wizard/TemplateSelection';
import TestDataCreation from './wizard/TestDataCreation';
import CriteriaDefinition from './wizard/CriteriaDefinition';
import EvaluationReview from './wizard/EvaluationReview';
import ExecutionResults from './wizard/ExecutionResults';

const WIZARD_STEPS = [
  { id: 'template', title: 'Template Selection', component: TemplateSelection },
  { id: 'test_data', title: 'Test Data', component: TestDataCreation },
  { id: 'criteria', title: 'Criteria', component: CriteriaDefinition },
  { id: 'review', title: 'Review', component: EvaluationReview },
  { id: 'execute', title: 'Execute', component: ExecutionResults }
];

const EvaluationWizard = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [wizardData, setWizardData] = useState({
    template: null,
    test_data: null,
    criteria: [],
    review: null
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isExecuting, setIsExecuting] = useState(false);

  // Complete wizard and create evaluation
  const completeWizard = useCallback(async () => {
    if (!sessionId) return;

    try {
      setLoading(true);
      const response = await wizardService.completeWizard(sessionId);

      if (response.success !== false) {
        toast.success('Evaluation created successfully!');
        // Move to execution step
        setCurrentStep(WIZARD_STEPS.length - 1);
        setIsExecuting(true);
        return response.evaluation || response.data;
      } else {
        toast.error(response.error || 'Failed to create evaluation');
        return null;
      }
    } catch (error) {
      console.error('Failed to complete wizard:', error);
      toast.error('Failed to create evaluation');
      return null;
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  const handleStepDataChange = useCallback((data) => {
    console.log('EvaluationWizard: handleStepDataChange called with:', data);
    setWizardData(prev => {
      const stepId = WIZARD_STEPS[currentStep].id;
      const newData = {
        ...prev,
        [stepId]: {
          ...(prev[stepId] || {}),
          ...data
        }
      };
      console.log('EvaluationWizard: New wizardData:', newData);
      return newData;
    });
  }, [currentStep]);

  // Expose wizardData for debugging
  useEffect(() => {
    window._wizardData = wizardData;
    console.log('Wizard Data Updated:', wizardData);
  }, [wizardData]);

  // Initialize wizard session
  useEffect(() => {
    const initializeWizard = async () => {
      if (!user) return;

      try {
        setLoading(true);
        const response = await wizardService.initializeWizard();

        if (response.success !== false) {
          setSessionId(response.session_id || response.data?.session_id);
        } else {
          toast.error('Failed to initialize wizard');
          navigate('/evaluations');
        }
      } catch (error) {
        console.error('Failed to initialize wizard:', error);
        toast.error('Failed to initialize wizard');
        navigate('/evaluations');
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading && user) {
      initializeWizard();
    }
  }, [user, authLoading, navigate]);

  // Save step data
  const saveStepData = useCallback(async (stepId, data) => {
    if (!sessionId) return false;

    try {
      setSaving(true);
      const response = await wizardService.saveWizardStep(sessionId, stepId, data);

      if (response.success !== false) {
        setWizardData(prev => ({
          ...prev,
          [stepId]: data
        }));
        return true;
      } else {
        // Handle validation errors specifically
        if (response.status === 422 && response.errors) {
          const errorMessages = Object.values(response.errors).flat();
          toast.error(`Validation failed: ${errorMessages.join(', ')}`);
        } else {
          toast.error(response.error || 'Failed to save step data');
        }
        return false;
      }
    } catch (error) {
      console.error('Failed to save step data:', error);
      toast.error('Failed to save step data');
      return false;
    } finally {
      setSaving(false);
    }
  }, [sessionId]);



  // Navigate to next step
  const nextStep = useCallback(async (dataOverride = null) => {
    const stepId = WIZARD_STEPS[currentStep].id;
    // Use override data if provided, otherwise fall back to state
    const currentStepData = dataOverride || wizardData[stepId];

    console.log('EvaluationWizard: nextStep called. Step:', currentStep, 'ID:', stepId);
    console.log('EvaluationWizard: Data to save:', currentStepData);

    // Validate current step data
    if (!currentStepData && currentStep < WIZARD_STEPS.length - 2) {
      toast.error('Please complete the current step before proceeding');
      return;
    }

    try {
      // Save current step data before proceeding
      if (currentStepData) {
        // Optimistically update state first to ensure UI feels responsive and data is preserved locally
        setWizardData(prev => ({
          ...prev,
          [stepId]: currentStepData
        }));

        const success = await saveStepData(stepId, currentStepData);
        if (!success) {
          return; // Don't proceed if save failed
        }
      }

      // Move to next step
      if (currentStep < WIZARD_STEPS.length - 1) {
        setCurrentStep(prev => prev + 1);
        window.scrollTo(0, 0);
      } else {
        completeWizard();
      }
    } catch (error) {
      console.error('Error in nextStep:', error);
      toast.error('Failed to proceed to next step');
    }
  }, [currentStep, wizardData, saveStepData, completeWizard]);

  // Navigate to previous step
  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  }, [currentStep]);

  // Go to specific step
  const goToStep = useCallback((stepIndex) => {
    console.log('EvaluationWizard: goToStep called with index:', stepIndex);
    if (stepIndex >= 0 && stepIndex < WIZARD_STEPS.length) {
      setCurrentStep(stepIndex);
    }
  }, []);

  // Calculate progress percentage
  const progressPercentage = ((currentStep + 1) / WIZARD_STEPS.length) * 100;

  // Show loading while checking authentication or initializing
  if (authLoading || loading) {
    return (
      <EvaluationLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <span className="ml-2">Loading...</span>
        </div>
      </EvaluationLayout>
    );
  }

  // Show login prompt if not authenticated
  if (!user) {
    return (
      <EvaluationLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center space-y-4">
            <h2 className="text-xl font-semibold">Authentication Required</h2>
            <p className="text-gray-600">Please log in to create evaluations.</p>
            <Button onClick={() => navigate('/login')}>
              Go to Login
            </Button>
          </div>
        </div>
      </EvaluationLayout>
    );
  }

  const CurrentStepComponent = WIZARD_STEPS[currentStep].component;

  return (
    <EvaluationLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="outline"
              onClick={() => navigate('/evaluations')}
              className="flex items-center space-x-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Evaluations</span>
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Create New Evaluation</h1>
              <p className="text-gray-600">
                Step {currentStep + 1} of {WIZARD_STEPS.length}: {WIZARD_STEPS[currentStep].title}
              </p>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="space-y-2">
          <Progress value={progressPercentage} className="h-2" />
          <div className="flex justify-between text-sm text-gray-500">
            {WIZARD_STEPS.map((step, index) => (
              <div
                key={step.id}
                className={`flex items-center space-x-1 cursor-pointer ${index === currentStep
                  ? 'text-blue-600 font-medium'
                  : index < currentStep
                    ? 'text-green-600'
                    : 'text-gray-400'
                  }`}
                onClick={() => goToStep(index)}
              >
                {index < currentStep ? (
                  <Check className="h-4 w-4" />
                ) : index === currentStep ? (
                  <div className="h-4 w-4 rounded-full bg-blue-600"></div>
                ) : (
                  <div className="h-4 w-4 rounded-full border-2 border-gray-300"></div>
                )}
                <span>{step.title}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 min-h-[500px]">
          <CurrentStepComponent
            wizardData={wizardData}
            currentStepData={wizardData[WIZARD_STEPS[currentStep].id]}
            onStepDataChange={handleStepDataChange}
            onNext={nextStep}
            onPrevious={previousStep}
            onComplete={completeWizard}
            sessionId={sessionId}
            isExecuting={isExecuting}
            setIsExecuting={setIsExecuting}
            saving={saving}
            loading={loading}
            goToStep={goToStep}
          />
        </div>
      </div>
    </EvaluationLayout>
  );
};


export default EvaluationWizard;
